'use client'

import { useState, useRef, useEffect } from 'react'
import { SearchIcon, CalculatorIcon, MessageSquareIcon, UserIcon, XIcon, ClockIcon, ChartIcon } from './Icons'

interface SearchResult {
  id: string
  title: string
  type: 'problem' | 'discussion' | 'user'
  subtitle?: string
  category?: string
}

interface SearchDropdownProps {
  placeholder?: string
  onSearch: (query: string) => void
  onSelect?: (result: SearchResult) => void
  className?: string
}

export default function SearchDropdown({ 
  placeholder = "Search problems, discussions, users...",
  onSearch,
  onSelect,
  className = ""
}: SearchDropdownProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('mathleet-recent-searches')
    if (saved) {
      setRecentSearches(JSON.parse(saved))
    }
  }, [])

  const handleInputChange = (value: string) => {
    setQuery(value)
    onSearch(value)
    
    if (value.length > 0) {
      setIsOpen(true)
      // Mock search results - in real app, this would be an API call
      setResults([
        { id: '1', title: `Problems containing "${value}"`, type: 'problem', subtitle: 'Search in problem titles and descriptions' },
        { id: '2', title: `Discussions about "${value}"`, type: 'discussion', subtitle: 'Search in discussion titles and content' },
        { id: '3', title: `Users named "${value}"`, type: 'user', subtitle: 'Search user profiles' },
      ])
    } else {
      setResults([])
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      saveRecentSearch(query)
      onSearch(query)
      setIsOpen(false)
    }
  }

  const saveRecentSearch = (search: string) => {
    const updated = [search, ...recentSearches.filter(s => s !== search)].slice(0, 5)
    setRecentSearches(updated)
    localStorage.setItem('mathleet-recent-searches', JSON.stringify(updated))
  }

  const handleRecentSearch = (search: string) => {
    setQuery(search)
    onSearch(search)
    setIsOpen(false)
  }

  const clearQuery = () => {
    setQuery('')
    onSearch('')
    setResults([])
    inputRef.current?.focus()
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'problem': return CalculatorIcon
      case 'discussion': return MessageSquareIcon
      case 'user': return UserIcon
      default: return SearchIcon
    }
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <form onSubmit={handleSubmit} className="relative">
        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className={`
            w-full pl-10 pr-10 py-3 text-lg
            bg-dark-700 border border-dark-600 rounded-lg
            text-white placeholder-gray-400
            hover:bg-dark-600 hover:border-dark-500
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
            transition-all duration-200
          `}
        />
        {query && (
          <button
            type="button"
            onClick={clearQuery}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
      </form>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-dark-700 border border-dark-600 rounded-lg dropdown-shadow animate-slide-up">
          <div className="py-2 max-h-96 overflow-auto">
            {query.length > 0 ? (
              <>
                {results.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => {
                      if (onSelect) onSelect(result)
                      saveRecentSearch(query)
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-dark-600 transition-colors duration-150"
                  >
                    {(() => {
                      const IconComponent = getTypeIcon(result.type)
                      return <IconComponent className="h-5 w-5 text-white" />
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">
                        {result.title}
                      </div>
                      {result.subtitle && (
                        <div className="text-xs text-gray-400 mt-0.5 truncate">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                    <SearchIcon className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
                <div className="border-t border-dark-600 mt-2 pt-2">
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-dark-600 transition-colors duration-150"
                  >
                    <SearchIcon className="h-4 w-4 text-primary-400" />
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        Search for &quot;{query}&quot;
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Press Enter to search all content
                      </div>
                    </div>
                  </button>
                </div>
              </>
            ) : (
              <>
                {recentSearches.length > 0 && (
                  <>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Recent Searches
                    </div>
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleRecentSearch(search)}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-dark-600 transition-colors duration-150"
                      >
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-white">{search}</span>
                      </button>
                    ))}
                    <div className="border-t border-dark-600 mt-2 pt-2"></div>
                  </>
                )}
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Popular Searches
                </div>
                {['AIME problems', 'Calculus integration', 'Geometry proofs', 'Number theory'].map((search, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleRecentSearch(search)}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-dark-600 transition-colors duration-150"
                  >
                    <ChartIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-white">{search}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}